import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Dialog, DialogContent } from './ui/dialog';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from './ui/alert-dialog';
import { Button } from './ui/button';
import { Skeleton } from './ui/skeleton';
import { ChevronLeft, ChevronRight, Download, User, Sparkles, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const PhotoGallery = ({ photos, loading, onPhotoDeleted }) => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const openLightbox = (index) => {
    setCurrentPhotoIndex(index);
    setLightboxOpen(true);
  };

  const goToPrevious = () => {
    setCurrentPhotoIndex((prev) => (prev > 0 ? prev - 1 : photos.length - 1));
  };

  const goToNext = () => {
    setCurrentPhotoIndex((prev) => (prev < photos.length - 1 ? prev + 1 : 0));
  };

  const handleDownload = (photo) => {
    window.open(`${API_BASE}/api/photos/${photo.id}/download`, '_blank');
  };

  const requestDelete = (photo) => {
    setDeleteTarget(photo);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axios.delete(`${API_BASE}/api/photos/${deleteTarget.id}`);
      toast.success('Photo deleted');

      if (lightboxOpen && photos[currentPhotoIndex]?.id === deleteTarget.id) {
        if (photos.length <= 1) {
          setLightboxOpen(false);
        } else {
          setCurrentPhotoIndex((prev) => (prev >= photos.length - 1 ? 0 : prev));
        }
      }

      if (onPhotoDeleted) {
        onPhotoDeleted(deleteTarget.id);
      }
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete photo. Please try again.');
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  };

  return (
    <section id="gallery" className="py-24 bg-gradient-to-b from-[#EEF4F0] to-white relative overflow-hidden" data-testid="gallery-section">
      {/* Decorative elements */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-[#D7C2A1]/10 rounded-full blur-3xl" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-primary/10 rounded-full blur-3xl" />
      
      <div className="section-container relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-20 space-y-6"
        >
          <div className="inline-flex items-center gap-2 px-6 py-2 bg-primary/10 backdrop-blur-sm rounded-full border border-primary/20">
            <Sparkles className="w-4 h-4 text-[#FFD700]" />
            <span className="text-sm text-primary font-semibold tracking-wider uppercase">Memories</span>
          </div>
          <h2 className="font-serif text-5xl sm:text-6xl font-normal text-primary">Photo Gallery</h2>
          <div className="h-1 w-32 bg-gradient-to-r from-transparent via-[#FFD700] to-transparent mx-auto" />
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            {photos.length > 0 
              ? `${photos.length} beautiful ${photos.length === 1 ? 'moment' : 'moments'} captured` 
              : 'Be the first to share your photos!'}
          </p>
        </motion.div>

        {loading ? (
          <div className="masonry-grid">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="masonry-item">
                <Skeleton className="w-full h-64 rounded-3xl" />
              </div>
            ))}
          </div>
        ) : photos.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-3xl border-2 border-dashed border-primary/30">
            <Sparkles className="w-16 h-16 text-primary/30 mx-auto mb-4" />
            <p className="text-xl text-muted-foreground mb-8">No photos uploaded yet.</p>
            <Button 
              size="lg"
              className="h-14 px-10 bg-primary text-white hover:bg-primary/90 rounded-full text-lg font-medium"
              onClick={() => {
                const element = document.querySelector('#upload');
                if (element) element.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              Upload First Photos
            </Button>
          </div>
        ) : (
          <div className="masonry-grid">
            {photos.map((photo, index) => (
              <motion.div
                key={photo.id}
                className="masonry-item"
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
              >
                <motion.div 
                  className="relative group cursor-pointer rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 border-4 border-white"
                  onClick={() => openLightbox(index)}
                  whileHover={{ y: -8 }}
                  data-testid="gallery-image-tile"
                >
                  <div className="relative overflow-hidden">
                    <img
                      src={`${API_BASE}${photo.thumbnail_url || photo.url}`}
                      alt={`Uploaded by ${photo.uploader_name}`}
                      loading="lazy"
                      className="w-full h-auto transform group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-primary/90 via-primary/40 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-300" />

                    {/* Delete button */}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        requestDelete(photo);
                      }}
                      data-testid={`gallery-delete-button-${photo.id}`}
                      aria-label={`Delete photo uploaded by ${photo.uploader_name}`}
                      className="absolute top-3 right-3 w-9 h-9 rounded-full bg-black/50 hover:bg-destructive text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-200 focus-ring z-10"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>

                    <div className="absolute inset-0 flex items-end p-6">
                      <div className="transform translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                        <div className="flex items-center gap-3 text-white">
                          <div 
                            className="w-10 h-10 rounded-full flex items-center justify-center shadow-lg"
                            style={{ 
                              backgroundImage: 'linear-gradient(135deg, #F9E27D 0%, #D4AF37 40%, #B8962E 60%, #F6E27A 100%)'
                            }}
                          >
                            <User className="w-5 h-5 text-primary" />
                          </div>
                          <span className="font-semibold text-lg">{photo.uploader_name}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Lightbox */}
      {photos.length > 0 && (
        <Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
          <DialogContent 
            className="max-w-[90vw] max-h-[90vh] w-auto h-auto p-0 bg-black border-none"
            data-testid="gallery-lightbox-dialog"
            aria-describedby="lightbox-description"
          >
            <span id="lightbox-description" className="sr-only">Photo gallery lightbox</span>
            <div className="relative w-full h-full bg-black">
              <div className="flex items-center justify-center p-4" style={{ minHeight: '80vh' }}>
                <img
                  src={`${API_BASE}${photos[currentPhotoIndex].url}`}
                  alt={`Photo by ${photos[currentPhotoIndex].uploader_name}`}
                  className="max-w-full max-h-[80vh] w-auto h-auto object-contain"
                />
              </div>

              <div className="absolute top-4 right-4 z-50 flex items-center gap-2">
                <Button
                  variant="secondary"
                  size="icon"
                  onClick={() => handleDownload(photos[currentPhotoIndex])}
                  data-testid="gallery-lightbox-download-button"
                >
                  <Download className="w-4 h-4" />
                </Button>
                <Button
                  variant="secondary"
                  size="icon"
                  onClick={() => requestDelete(photos[currentPhotoIndex])}
                  data-testid="gallery-lightbox-delete-button"
                  className="hover:bg-destructive hover:text-destructive-foreground"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>

              {photos.length > 1 && (
                <>
                  <Button
                    variant="secondary"
                    size="icon"
                    className="absolute left-4 top-1/2 -translate-y-1/2 z-50"
                    onClick={goToPrevious}
                    data-testid="gallery-lightbox-prev-button"
                  >
                    <ChevronLeft className="w-6 h-6" />
                  </Button>
                  <Button
                    variant="secondary"
                    size="icon"
                    className="absolute right-4 top-1/2 -translate-y-1/2 z-50"
                    onClick={goToNext}
                    data-testid="gallery-lightbox-next-button"
                  >
                    <ChevronRight className="w-6 h-6" />
                  </Button>
                </>
              )}

              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 to-transparent p-8 text-white">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-10 h-10 rounded-full flex items-center justify-center shadow-lg"
                      style={{ 
                        backgroundImage: 'linear-gradient(135deg, #F9E27D 0%, #D4AF37 40%, #B8962E 60%, #F6E27A 100%)'
                      }}
                    >
                      <User className="w-5 h-5 text-primary" />
                    </div>
                    <span className="font-semibold text-lg">{photos[currentPhotoIndex].uploader_name}</span>
                  </div>
                  <span className="text-lg text-white/80">
                    {currentPhotoIndex + 1} / {photos.length}
                  </span>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="gallery-delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this photo?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget && (
                <>This will permanently remove the photo uploaded by <span className="font-medium text-foreground">{deleteTarget.uploader_name}</span> from the gallery. This can't be undone.</>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="gallery-delete-cancel-button" disabled={deleting}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={deleting}
              data-testid="gallery-delete-confirm-button"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? 'Deleting...' : 'Delete Photo'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
};

export default PhotoGallery;
